// Yup validation schemas. The launch schema is a factory that takes the runtime surveyRecipients count.

import * as Yup from "yup";
import dayjs from "dayjs";
import { RESPONSE_OPTION_MAX_LENGTH } from "utils/consts";

const answerTextValidation = Yup.string().max(
    RESPONSE_OPTION_MAX_LENGTH,
    `Response option cannot exceed ${RESPONSE_OPTION_MAX_LENGTH} characters`,
);

const answerSchema = Yup.object({
    text: answerTextValidation.notRequired(),
    position: Yup.number().required(),
});

const answerLaunchSchema = Yup.object({
    text: answerTextValidation.required(),
    position: Yup.number().required(),
});

const QuestionType = Yup.object({
    answer_type: Yup.string().required(),
    description: Yup.string().notRequired(),
    is_allow_custom: Yup.boolean().required(),
    is_random_order: Yup.boolean().required(),
    is_view_all: Yup.boolean().required(),
    position: Yup.number().required(),
    question_type: Yup.string().required(),
    text: Yup.string().notRequired(),
    preset_answers: Yup.array().of(answerSchema),
    answer_limit: Yup.number().required(),
});

const QuestionLaunchType = Yup.object().shape({
    answer_type: Yup.string().required(),
    description: Yup.string().notRequired(),
    is_allow_custom: Yup.boolean().required(),
    is_random_order: Yup.boolean().required(),
    is_view_all: Yup.boolean().required(),
    position: Yup.number().required(),
    question_type: Yup.string().required(),
    text: Yup.string().required("Each Question needs text!"),
    preset_answers: Yup.array()
        .of(answerLaunchSchema)
        .when("question_type", {
            is: (type) => type !== "mixed" && type !== "freeform",
            then: (Schema) =>
                Schema.test(
                    "if-preset-min-2-asnwers",
                    "Preset answer type questions require at least 2 responses",
                    (value) => value && value.length >= 2,
                ),
        }),
    answer_limit: Yup.number()
        .min(1, "Question Position must be at least 1")
        .max(200),
});

export const validationSchemaSurvey = Yup.object({
    name: Yup.string().required("Survey Name Required"),
    description: Yup.string().notRequired(),
    start_date: Yup.string()
        .notRequired()
        .test(
            "Start Datetime",
            "Invalid Start Time",
            (value, testContext) => {
                if (!dayjs(value).isValid()) {
                    return testContext.createError({
                        message: "Please enter a valid Start Time!",
                    });
                } else if (dayjs(value).isBefore(dayjs(), "day")) {
                    return testContext.createError({
                        message: "Start Time cannot be in the Past!",
                    });
                } else {
                    return true;
                }
            },
        ),
    end_date: Yup.string()
        .notRequired()
        .test("End Datetime", "Invalid End Time", (value, testContext) => {
            if (!dayjs(value).isValid()) {
                return testContext.createError({
                    message: "Please enter a valid End Time!",
                });
            } else if (dayjs(value).isBefore(dayjs(), "day")) {
                return testContext.createError({
                    message: "End Time cannot be in the Past!",
                });
            } else {
                return true;
            }
        }),
    start_text: Yup.string().notRequired(),
    end_text: Yup.string().notRequired(),
    questions: Yup.array().of(QuestionType),
});

// Factory: launch schema needs the current recipients list to validate
// "no valid participants" rule. Pass `surveyRecipients` from runtime state.
export const createLaunchValidationSchema = ({ surveyRecipients }) =>
    Yup.object({
        name: Yup.string().required("Survey Name Required"),
        description: Yup.string().notRequired(),
        start_date: Yup.string()
            .notRequired()
            .test(
                "Start Datetime",
                "Invalid Start Time",
                (value, testContext) => {
                    const { pausedValidation } =
                        testContext.options.context || {};
                    if (!dayjs(value).isValid()) {
                        return testContext.createError({
                            message: "Please enter a valid Start Time!",
                        });
                    } else if (
                        !pausedValidation &&
                        dayjs(value).isBefore(dayjs(), "day")
                    ) {
                        return testContext.createError({
                            message: "Start Time cannot be in the Past!",
                        });
                    } else {
                        return true;
                    }
                },
            ),
        is_allow_anonymous: Yup.boolean().test(
            "Participants",
            "No Valid Participants",
            (value, testContext) => {
                if (!value && (!surveyRecipients || surveyRecipients.length < 1)) {
                    return testContext.createError({
                        message:
                            "Either allow Anonymous Participants or Add Them!",
                    });
                } else {
                    return true;
                }
            },
        ),
        end_date: Yup.string()
            .required("Please ensure the survey has a valid end date")
            .test("End Datetime", "Invalid End Time", (value, testContext) => {
                const { pausedValidation } =
                    testContext.options.context || {};
                if (!dayjs(value).isValid()) {
                    return testContext.createError({
                        message: "Please enter a valid End Time!",
                    });
                } else if (
                    !pausedValidation &&
                    dayjs(value).isBefore(dayjs(), "day")
                ) {
                    return testContext.createError({
                        message: "End Time cannot be in the Past!",
                    });
                } else {
                    return true;
                }
            })
            .test(
                "End Date after Start Date",
                "End Date must be after Start Date!",
                function (value) {
                    const { start_date } = this.parent;
                    return dayjs(value).isAfter(dayjs(start_date));
                },
            ),
        start_text: Yup.string().required("Start Text Required"),
        end_text: Yup.string().required("End Text Required"),
        questions: Yup.array(QuestionLaunchType).min(1),
    });

export const validationSchemaTheme = Yup.object({
    name: Yup.string().required("Theme Name Required"),
    description: Yup.string().notRequired(),
});

export const validationSchemaAnswerThemes = Yup.object({
    themes: Yup.array(),
});

export const validationSchemaThemeAnswers = Yup.object({
    theme: Yup.string(),
    answers_preset: Yup.array(),
    answers_custom: Yup.array(),
});
